import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { api } from '@/services/api';
import { portalApi } from '@/services/portal-api';

export type ThemeMode = 'dark' | 'light';

interface ThemeState {
  theme: ThemeMode;
  isLoading: boolean;
  error: string | null;

  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  syncTheme: (theme: ThemeMode) => void;
  saveTheme: (theme: ThemeMode) => Promise<void>;
  savePortalTheme: (theme: ThemeMode) => Promise<void>;
}

function applyTheme(theme: ThemeMode) {
  const root = document.documentElement;
  if (theme === 'light') {
    root.setAttribute('data-theme', 'light');
  } else {
    root.removeAttribute('data-theme');
  }
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      isLoading: false,
      error: null,

      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme, error: null });
      },

      toggleTheme: () => {
        const next = get().theme === 'dark' ? 'light' : 'dark';
        get().setTheme(next);
      },

      syncTheme: (theme) => {
        applyTheme(theme);
        set({ theme, error: null });
      },

      saveTheme: async (theme) => {
        // Apply immediately (optimistic) so the UI always responds to the
        // click; the account is the source of truth for the *next* login,
        // not a gate on this one's visual feedback.
        applyTheme(theme);
        set({ theme, isLoading: true, error: null });
        try {
          await api.patch('/users/me/tema', { tema: theme });
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Erro ao salvar tema';
          set({ error: message, isLoading: false });
        }
      },

      savePortalTheme: async (theme) => {
        applyTheme(theme);
        set({ theme, isLoading: true, error: null });
        try {
          await portalApi.patch('/portal/me/tema', { tema: theme });
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Erro ao salvar tema';
          set({ error: message, isLoading: false });
        }
      },
    }),
    {
      name: 'theme-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ theme: state.theme }),
      onRehydrateStorage: () => (state) => {
        if (state?.theme) {
          applyTheme(state.theme);
        }
      },
    }
  )
);
